import { useState } from 'react';
import {
  Button, Dialog, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableHead, TableRow, Chip,
} from '@mui/material';
import { sendServerRequest } from '../../server/request';
import { URLS } from '../../env/urls';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';

interface CltConsult {
  provider: 'soma' | 'v8';
  bancalizadora: string | null;
  status: string | null;
  cpf: string;
  consultId: string | null;
  createdAt: string;
}

/** Cor do chip de status conforme o desfecho da consulta. */
function statusColor(status?: string | null): 'success' | 'error' | 'warning' | 'default' {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS') return 'success';
  if (s === 'FAILED' || s === 'REJECTED') return 'error';
  if (s.startsWith('WAITING') || s === 'CONSENT_APPROVED') return 'warning';
  return 'default';
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR');
}

/**
 * Botão (para o menu do chat) que abre o histórico de consultas CLT feitas por
 * este chat (soma + v8), com provider e status de cada uma.
 */
export function CltConsultHistory({ chatId }: { chatId: number }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consults, setConsults] = useState<CltConsult[] | undefined>(undefined);

  return (
    <>
      <Button fullWidth variant="text" onClick={load}>Histórico de consultas CLT</Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Histórico de consultas CLT</DialogTitle>
        <DialogContent>
          {loading ? <CircularLoading /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!consults || consults.length === 0) && (
                  <TableRow><TableCell colSpan={3} align="center" style={{ color: '#00000080' }}>Nenhuma consulta.</TableCell></TableRow>
                )}
                {(consults ?? []).map((c, i) => (
                  <TableRow key={`${c.provider}-${c.consultId ?? i}`} hover>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(c.createdAt)}</TableCell>
                    <TableCell>{c.provider === 'soma' ? `soma${c.bancalizadora ? ` (${c.bancalizadora})` : ''}` : 'v8'}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" color={statusColor(c.status)} label={c.status ?? '—'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  function load() {
    setOpen(true);
    if (consults) return;
    setLoading(true);
    sendServerRequest('GET', URLS.fgtsChat.cltConsults(chatId)).then((e) => {
      setConsults(e.data?.data?.consults ?? []);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      openGlobalSnack('Não foi possível carregar o histórico de consultas CLT.', 'error');
    });
  }
}
